import React, { useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons'
import { Checkbox } from 'spa/member/components/Checkbox'
import { CheckBoxProps } from 'spa/member/components/Checkbox/styled'

export type StyledSearchConditionDropdownProps = {
  header: string,
  options?: Array<CheckBoxProps>,
  groupedOptions?: Array<OptionGroup>,
  columns: number,
}

export type StyledSearchConditionDropdownPropsWithId = StyledSearchConditionDropdownProps & {
  id: number
}

const DropdownContainer = styled.div`
  width: 100%;
`

const DropdownHeaderContainer = styled.div`
  font-size: .875rem;
  margin-bottom: -1px;
  height: calc(100% + 1px);
  border-top: 1px solid ${props => props.theme.members.colors.silver._30};
  border-bottom: 1px solid ${props => props.theme.members.colors.silver._30};
`

const ClosedDropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.members.colors.silver._10};
  cursor: pointer;
`

const OpenedDropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: ${props => props.theme.members.colors.silver._30};
  border-bottom: 1px solid ${props => props.theme.members.colors.silver._30};
  cursor: pointer;
`

interface DropdownContentContainerProps {
  isOpen: boolean;
}

const DropdownContentContainer = styled.div<DropdownContentContainerProps>`
  position: relative;
  background: ${props => props.theme.members.colors.silver._10};
  padding: ${props => props.isOpen ? '1.5rem' : '0 1.5rem'};
  display: grid;
  transition: 250ms all ease;
  grid-template-rows: ${props => props.isOpen ? '1fr' : '0fr'};
`

const StyledDropdownWrapper = styled.div`
  overflow: hidden;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const StyledGroupTitle = styled.div`
  font-size: .875rem;
  font-weight: 700;
  line-height: 1.4875rem;
  text-align: left;
  margin: 0 0 .75rem;
  width: 100%;
`

const StyledDropdownGroup = styled.div`
  width: 100%;
  padding: 0;
`

const StyledUl = styled.ul<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(1, auto);
  list-style: none;
  padding: 0;
  gap: 1rem;

  @media ${props => props.theme.media.sm} {
    grid-template-columns: repeat(${props => props.columns}, 1fr);
    gap: .5rem;
  }
`

const MultipleColumnLi = styled.li`
  width: 100%;

  font-size: .875rem;
  font-weight: 400;
  text-align: left;

  display: flex;
  align-items: center;
`

type OptionProps = {
  column: number,
  options?: Array<CheckBoxProps>,
  groupedOptions?: Array<OptionGroup>
}

export type OptionGroup = {
  title: string,
  options: CheckBoxProps[]
}

const StyledOptions: React.FC<OptionProps> = (props: OptionProps) => {
  if (props.options?.length) {
    return (
      <StyledDropdownGroup>
        <StyledUl columns={props.column}>
          {props.options.map((option: CheckBoxProps) => (
            <MultipleColumnLi key={option.value}>
              <Checkbox {...option} />
            </MultipleColumnLi>
          ))}
        </StyledUl>
      </StyledDropdownGroup>
    )
  } else {
    return (
      <>
        {
          props.groupedOptions.map((optionGroup: OptionGroup, index: number) => {
            return (
              <StyledDropdownGroup key={index}>
                {optionGroup.title && <StyledGroupTitle>{optionGroup.title}</StyledGroupTitle>}
                <StyledUl columns={props.column}>
                  {optionGroup.options.map((option: CheckBoxProps) => (
                    <MultipleColumnLi key={option.value}>
                      <Checkbox {...option} />
                    </MultipleColumnLi>
                  ))}
                </StyledUl>
              </StyledDropdownGroup>
            )
          })
        }
      </>
    )
  }
}

export const StyledSearchConditionDropdown: React.FC<StyledSearchConditionDropdownProps> = (props: StyledSearchConditionDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleToggle = () => {
    setIsOpen(!isOpen)
  }

  return (
    <DropdownContainer>
      <DropdownHeaderContainer>
        {isOpen ? <OpenedDropdownHeader onClick={handleToggle}>{props.header}<FontAwesomeIcon icon={faAngleUp} color='silver' /></OpenedDropdownHeader> : <ClosedDropdownHeader onClick={handleToggle}>{props.header}<FontAwesomeIcon icon={faAngleDown} color='silver' /></ClosedDropdownHeader>}
      </DropdownHeaderContainer>
      <DropdownContentContainer isOpen={isOpen}>
        <StyledDropdownWrapper>
          <StyledOptions column={props.columns} options={props.options} groupedOptions={props.groupedOptions} />
        </StyledDropdownWrapper>
      </DropdownContentContainer>
    </DropdownContainer>
  )
}